import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, MapPin, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !message) {
      toast.error(t('contact_error'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { name, email, subject, message });
      toast.success(t('contact_success'));
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-navy-950">
      <nav className="fixed top-0 w-full z-50 backdrop-blur-lg border-b border-white/5 bg-navy-950/80">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_7d9bc2e5-ff38-4e06-b101-9f0eb41c2616/artifacts/w350as9h_LOGOvrn-02.png" alt="VerbNotes" className="h-12" />
          </Link>
          <LanguageSelector variant="ghost" />
        </div>
      </nav>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto px-6 pt-28 pb-16"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-cyan hover:text-cyan-300 mb-8 text-sm" data-testid="back-home-link">
          <ArrowLeft size={16} /> {t('contact_back')}
        </Link>

        <h1 className="font-heading text-4xl text-white mb-2" data-testid="contact-title">{t('contact_title')}</h1>
        <p className="text-slate-400 mb-10">{t('contact_subtitle')}</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="space-y-6">
            <div className="glass rounded-2xl p-6">
              <div className="w-10 h-10 rounded-xl bg-cyan/10 flex items-center justify-center mb-3">
                <Mail className="text-cyan" size={20} />
              </div>
              <h3 className="text-white font-medium mb-1">{t('contact_email')}</h3>
              <p className="text-slate-400 text-sm">support@verbnotes.com</p>
            </div>

            <div className="glass rounded-2xl p-6">
              <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center mb-3">
                <MapPin className="text-gold" size={20} />
              </div>
              <h3 className="text-white font-medium mb-1">{t('contact_company')}</h3>
              <p className="text-slate-400 text-sm">{t('contact_address')}</p>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="glass rounded-2xl p-6 md:p-8">
              <h2 className="text-xl text-white font-medium mb-6">{t('contact_send')}</h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('contact_name')} *</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('contact_name_placeholder')} className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500" data-testid="contact-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('contact_email')} *</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('contact_email_placeholder')} className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500" data-testid="contact-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('contact_subject')}</Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t('contact_subject_placeholder')} className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500" data-testid="contact-subject-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('contact_message')} *</Label>
                  <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t('contact_message_placeholder')} rows={5} className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500 resize-none" data-testid="contact-message-input" />
                </div>
                <Button type="submit" disabled={loading} className="bg-cyan text-navy-950 hover:bg-cyan-300 rounded-full px-8 py-5 font-semibold shadow-[0_0_20px_rgba(0,212,255,0.3)]" data-testid="contact-submit-btn">
                  {loading ? t('contact_sending') : (<><Send size={16} className="mr-2" />{t('contact_send')}</>)}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </motion.div>

      <footer className="border-t border-white/5 py-6 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-slate-500 text-sm">&copy; 2026 VerbNotes. {t('contact_company')}.</p>
        </div>
      </footer>
    </div>
  );
}
